import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../config';
import '../styles/procurement-admin.css';

function newId() {
  const bytes=crypto.getRandomValues(new Uint8Array(16));bytes[6]=(bytes[6]&15)|64;bytes[8]=(bytes[8]&63)|128;
  const h=Array.from(bytes,b=>b.toString(16).padStart(2,'0')).join('');
  return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20)}`;
}

function deactivationReadModel(warehouse) {
  const counts=[
    ['active_wm_assignment_count','Warehouse Manager assignment','Warehouse Manager assignments'],
    ['active_aggregator_mapping_count','Aggregator mapping','Aggregator mappings'],
    ['in_flight_dispatch_count','in-flight dispatch','in-flight dispatches'],
  ];
  const validCounts=counts.every(([field])=>Number.isSafeInteger(warehouse[field])&&warehouse[field]>=0);
  if(typeof warehouse.can_deactivate!=='boolean'||!validCounts) return {verified:false,canDeactivate:false,reasons:[]};
  const blocked=counts.some(([field])=>warehouse[field]>0);
  if(warehouse.can_deactivate===blocked) return {verified:false,canDeactivate:false,reasons:[]};
  return {
    verified:true,
    canDeactivate:warehouse.can_deactivate,
    reasons:counts.filter(([field])=>warehouse[field]>0).map(([field,singular,plural])=>
      `${warehouse[field]} ${warehouse[field]===1?singular:plural}`),
  };
}

export default function ProcurementWarehouses() {
  const [data,setData]=useState(null);
  const [name,setName]=useState('');
  const [address,setAddress]=useState('');
  const [message,setMessage]=useState('');
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState(false);
  const pending=useRef(null);

  const request=useCallback(async(path,body)=>{
    const response=await fetch(`${API_BASE}/procurement/v1${path}`,{
      method:body?'POST':'GET',
      headers:{Authorization:localStorage.getItem('token'),'Content-Type':'application/json'},
      ...(body?{body:JSON.stringify(body)}:{}),
    });
    const result=await response.json();
    if(!response.ok) throw new Error(result.message||'Procurement request failed');
    return result;
  },[]);

  const load=useCallback(async()=>{
    setLoading(true);
    try {
      const result=await request('/bootstrap');
      setData(result);
      setMessage('');
      return result;
    } catch(error) {
      setData(null);
      setMessage(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  },[request]);

  useEffect(()=>{load().catch(()=>{});},[load]);

  function commandFor(action,entityId,payload,retryScope=entityId) {
    const fingerprint=JSON.stringify({action,retryScope,payload});
    if(!pending.current||pending.current.fingerprint!==fingerprint) {
      pending.current={fingerprint,command:{command_id:newId(),entity_id:entityId,action,data:payload}};
    }
    return pending.current.command;
  }

  async function createWarehouse(event) {
    event.preventDefault();
    const cleanName=name.trim();
    if(!cleanName) {setMessage('Warehouse name is required.');return;}
    setBusy(true);setMessage('');
    try {
      const command=commandFor('master.warehouses',newId(),{name:cleanName,address:address.trim()},'create');
      await request('/commands',command);
      pending.current=null;
      setName('');setAddress('');
      try {
        await load();
        setMessage('Warehouse created and active.');
      } catch {
        setMessage('Warehouse created, but the list could not be refreshed. Please refresh this page.');
      }
    } catch(error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(warehouse,active) {
    const preparedCount=(data?.dispatches||[]).filter(dispatch=>
      dispatch.warehouse_id===warehouse.id&&dispatch.state==='PREPARED').length;
    const prompt=active
      ? `Activate warehouse “${warehouse.name}”?`
      : preparedCount>0
        ? `The currently loaded data shows ${preparedCount} prepared dispatch(es) using this warehouse. Those dispatches cannot depart while the warehouse is inactive. The warehouse can be reactivated later, or the Aggregator may cancel those prepared dispatches.\n\nDeactivate “${warehouse.name}”?`
        : `Deactivate “${warehouse.name}”? It will remain in history, but cannot be selected for new dispatches.`;
    if(!window.confirm(prompt)) return;

    setBusy(true);setMessage('');
    try {
      const command=commandFor('warehouse.set-active',warehouse.id,{active});
      await request('/commands',command);
      pending.current=null;
      try {
        await load();
        setMessage(`Warehouse ${active?'activated':'deactivated'}.`);
      } catch {
        setMessage(`Warehouse ${active?'activated':'deactivated'}, but the list could not be refreshed. Please refresh this page.`);
      }
    } catch(error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function submitCommand(action,entityId,payload,successMessage) {
    setBusy(true);setMessage('');
    try {
      const command=commandFor(action,entityId,payload);
      await request('/commands',command);
      pending.current=null;
      try { await load(); setMessage(successMessage); }
      catch { setMessage(`${successMessage} The list could not be refreshed; please refresh this page.`); }
      return true;
    } catch(error) { setMessage(error.message); }
    finally { setBusy(false); }
    return false;
  }

  function managerAssignments(manager) {
    return Array.isArray(manager.assigned_warehouses)?manager.assigned_warehouses:[];
  }
  function aggregatorMappings(aggregatorId) {
    return (data?.aggregator_warehouse_mappings||[]).filter(row=>row.aggregator_id===aggregatorId&&row.mapping_id);
  }

  async function changeAssignment(manager,warehouse,existing) {
    const active=Boolean(existing?.active);
    if(active&&!window.confirm(`Unassign ${warehouse.name} from ${manager.name}? Historical receipts will remain unchanged.`)) return;
    await submitCommand(active?'warehouse.manager.assignment.deactivate':'warehouse.manager.assignment.grant',
      active?existing.assignment_id:newId(),active?{}:{membership_id:manager.membership_id,warehouse_id:warehouse.id},
      active?'Warehouse assignment removed.':'Warehouse assigned.');
  }

  async function changeMapping(aggregator,warehouse,existing) {
    const active=Boolean(existing?.active);
    if(active&&!window.confirm(`Unmap ${warehouse.name} from ${aggregator.name}? Existing dispatch history will remain unchanged.`)) return;
    await submitCommand(active?'warehouse.aggregator.mapping.deactivate':'warehouse.aggregator.mapping.grant',
      active?existing.mapping_id:newId(),active?{}:{aggregator_id:aggregator.id,warehouse_id:warehouse.id},
      active?'Aggregator warehouse mapping removed.':'Warehouse mapped to Aggregator.');
  }

  if(loading&&!data) return <section className="proc-admin proc-admin--warehouses" aria-label="Procurement warehouses"><h2>Procurement Warehouses</h2><p>Loading warehouse data…</p></section>;
  return <section className="proc-admin proc-admin--warehouses" aria-label="Procurement warehouses">
    <h2>Procurement Warehouses</h2>
    <p>Warehouses belong to this FPO and remain in history when inactive.</p>
    {message&&<p className="proc-admin-warehouse-message" role="status">{message}</p>}
    {!data&&<button disabled={busy} onClick={()=>load().catch(()=>{})}>Refresh</button>}
    {data&&<>
      <section className="proc-admin-warehouse-master" aria-label="Warehouse master">
      <h3>Warehouse master</h3>
      <form className="proc-admin-warehouse-form" onSubmit={createWarehouse}>
        <h3>Create warehouse</h3>
        <label>Warehouse name *<input aria-label="Warehouse name" required maxLength={2000} value={name} onChange={event=>setName(event.target.value)} /></label>
        <label>Address<input aria-label="Address" value={address} onChange={event=>setAddress(event.target.value)} /></label>
        <button type="submit" disabled={busy}>{busy?'Saving…':'Create warehouse'}</button>
      </form>
      <div className="proc-admin-warehouse-toolbar"><button disabled={busy||loading} onClick={()=>load().catch(()=>{})}>Refresh</button></div>
      {(data.warehouses||[]).length===0?<p>No Procurement warehouses have been configured yet.</p>:<div className="proc-admin-warehouse-table-scroll" role="region" aria-label="Warehouse list" tabIndex={0}>
        <table className="proc-admin-warehouse-table"><thead><tr><th>Name</th><th>Address</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>{data.warehouses.map(warehouse=>{
            const active=warehouse.active===true;
            const deactivation=active?deactivationReadModel(warehouse):null;
            return <tr key={warehouse.id}>
            <td>{warehouse.name}</td><td>{warehouse.address||'—'}</td>
            <td><span className={active?'proc-admin-warehouse-status proc-admin-warehouse-status--active':'proc-admin-warehouse-status proc-admin-warehouse-status--inactive'}>{active?'Active':'Inactive'}</span></td>
            <td className="proc-admin-warehouse-actions">
              <button disabled={busy||(active&&(!deactivation.verified||!deactivation.canDeactivate))} onClick={()=>changeStatus(warehouse,!active)}>{active?'Deactivate':'Activate'}</button>
              {active&&!deactivation.verified&&<p className="proc-admin-deactivation-guidance">Deactivation availability could not be verified. Refresh the page.</p>}
              {active&&deactivation.verified&&!deactivation.canDeactivate&&<div className="proc-admin-deactivation-guidance"><strong>Cannot deactivate:</strong><ul>{deactivation.reasons.map(reason=><li key={reason}>{reason}</li>)}</ul></div>}
            </td>
          </tr>})}</tbody>
        </table>
      </div>}
      </section>
      <section className="proc-admin-warehouse-section" aria-label="Warehouse Managers">
        <h3>Warehouse Managers</h3>
        <p className="proc-admin-warehouse-guidance">Warehouse Manager accounts are managed under Users &amp; Access &gt; Warehouse Managers.</p>
        {(data.warehouse_managers||[]).length===0?<p>No Warehouse Managers configured.</p>:(data.warehouse_managers||[]).map(manager=>{
          const assignments=managerAssignments(manager);
          return <article className="proc-admin-assignment-card" key={manager.membership_id} aria-label={`Warehouse Manager ${manager.name}`}>
            <h4>{manager.name}</h4>
            <h5>Assigned warehouses</h5>
            <div className="proc-admin-assignment-items">
            {(data.warehouses||[]).filter(warehouse=>warehouse.active||assignments.some(row=>row.warehouse_id===warehouse.id&&row.active)).map(warehouse=>{
              const assignment=assignments.find(row=>row.warehouse_id===warehouse.id);
              const active=Boolean(assignment?.active);
              const canGrant=warehouse.active&&manager.membership_active&&manager.actor_active;
              const reasons=[
                warehouse.active===false?'Warehouse is inactive':null,
                manager.membership_active===false?'Warehouse Manager membership is inactive':null,
                manager.actor_active===false?'Warehouse Manager account is inactive':null,
              ].filter(Boolean);
              const hint=!active&&!canGrant?(reasons.join('. ')||'Not currently eligible for a new assignment'):null;
              return <label className="proc-admin-assignment-item" key={warehouse.id}>
                <input type="checkbox" aria-label={`${warehouse.name} assigned to ${manager.name}`} checked={active} disabled={busy||(!active&&!canGrant)}
                  onChange={()=>changeAssignment(manager,warehouse,assignment)} /> <span className="proc-admin-assignment-copy">{warehouse.name}{warehouse.active?'':' (inactive)'}{assignment&&!active?' (unassigned)':''}{hint&&<small className="proc-admin-assignment-hint">{hint}</small>}</span>
              </label>;
            })}
            </div>
          </article>;
        })}
      </section>
      <section className="proc-admin-warehouse-section" aria-label="Aggregator Warehouse Mapping">
        <h3>Aggregator Warehouse Mapping</h3>
        <p>Only active warehouses can be newly mapped. Existing dispatch history is retained when mappings change.</p>
        {(data.aggregators||[]).length===0?<p>No Aggregators configured.</p>:(data.aggregators||[]).map(aggregator=>{
          const mappings=aggregatorMappings(aggregator.id);
          return <article className="proc-admin-assignment-card" key={aggregator.id} aria-label={`Aggregator ${aggregator.name}`}>
            <h4>{aggregator.name}</h4>
            <div className="proc-admin-assignment-items">
            {(data.warehouses||[]).filter(warehouse=>warehouse.active||mappings.some(row=>row.warehouse_id===warehouse.id&&row.active)).map(warehouse=>{
              const mapping=mappings.find(row=>row.warehouse_id===warehouse.id);
              const active=Boolean(mapping?.active);
              const hint=!active&&!warehouse.active?(warehouse.active===false?'Warehouse is inactive':'Not currently eligible for a new mapping'):null;
              return <label className="proc-admin-assignment-item" key={warehouse.id}>
                <input type="checkbox" aria-label={`${warehouse.name} mapped to ${aggregator.name}`} checked={active} disabled={busy||(!active&&!warehouse.active)}
                  onChange={()=>changeMapping(aggregator,warehouse,mapping)} /> <span className="proc-admin-assignment-copy">{warehouse.name}{warehouse.active?'':' (inactive)'}{mapping&&!active?' (unmapped)':''}{hint&&<small className="proc-admin-assignment-hint">{hint}</small>}</span>
              </label>;
            })}
            </div>
          </article>;
        })}
      </section>
    </>}
  </section>;
}
